import React from 'react';
import type { Client } from '../../types';
import { ClientCard } from './ClientCard';
import { LoadingSpinner } from '../shared';
import styles from './ClientList.module.css';

interface ClientListProps {
  clients: Client[];
  isLoading: boolean;
  error: string | null;
  onViewPrograms: (client: Client) => void;
  onCheckin: (client: Client) => void;
  onDelete: (clientId: string) => void;
  onAddNew: () => void;
  onGenerateProgram?: (client: Client) => void;
}

export const ClientList: React.FC<ClientListProps> = ({
  clients,
  isLoading,
  error,
  onViewPrograms,
  onCheckin,
  onDelete,
  onAddNew,
  onGenerateProgram,
}) => {
  if (isLoading) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner size="large" message="Loading clients..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <p>{error}</p>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className={styles.empty}>
        <div className={styles.emptyIcon}>👋</div>
        <h3 className={styles.emptyTitle}>No clients yet</h3>
        <p className={styles.emptyText}>
          Add your first client to start generating AI-powered training programs.
        </p>
        <button className={styles.emptyButton} onClick={onAddNew}>
          Add Client
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Your Clients</h2>
        <span className={styles.count}>{clients.length} client{clients.length !== 1 ? 's' : ''}</span>
      </div>
      
      <div className={styles.grid}>
        {clients.map(client => (
          <ClientCard
            key={client.id}
            client={client}
            onViewPrograms={onViewPrograms}
            onCheckin={onCheckin}
            onDelete={onDelete}
            onGenerateProgram={onGenerateProgram}
          />
        ))}
      </div>
    </div>
  );
};
